'use client';

import { useTranslations } from 'next-intl';
import React, { type ReactNode, useId, useMemo, useState } from 'react';
import ReactSelect, { createFilter, type FilterOptionOption, type MenuPlacement } from 'react-select';
import { type OnChangeValue } from 'react-select/dist/declarations/src/types';

import clsx from 'clsx';

import { Checkbox } from '@/components/common/Checkbox';
import { type ControlState } from '@/components/common/Control';
import { Flex } from '@/components/common/Flex';
import { CrossIcon } from '@/components/common/icons/CrossIcon';
import { ToggleArrow } from '@/components/common/ToggleArrow';
import { type WithClassName } from '@/types/react';
import { getNodeText } from '@/utils/react';
import { isArray, isDefined } from '@/utils/utils';

import styles from './Select.module.scss';

type SelectItemNode = {
  node: ReactNode;
};

export type SelectableValue = string | number | { id: string };

export type SelectItem<Value> = WithClassName & {
  value: Value;
  selectedNode?: ReactNode;
} & (Value extends ReactNode ? Partial<SelectItemNode> : SelectItemNode);

export type SelectProps<Value, CurrentValue extends Value | Value[] = Value> = WithClassName & {
  variant?: 'default' | 'box';
  value?: CurrentValue extends NoInfer<Value> ? CurrentValue | null : CurrentValue;
  options: SelectItem<Value>[];
  isMulti?: boolean;
  placeholder?: ReactNode;
  state?: ControlState;
  disabled?: boolean;
  emptyListNode?: ReactNode;
  isSearchable?: boolean;
  menuPlacement?: MenuPlacement;
  onChange?: (selectedOptions: NoInfer<CurrentValue>) => void;
  /** Overrides react-select internal ids; defaults to a stable `useId()` (fixes SSR hydration). */
  instanceId?: string;
  menuClassName?: string;
  menuPortalTarget?: HTMLElement | null;
  fitMenuContent?: boolean;
  closeMenuOnSelect?: boolean;
};

const getRawValue = <Value extends SelectableValue>(value: Value) =>
  typeof value === 'object' && 'id' in value ? value.id : value;

const getOptionDisplayEntity = <Value extends SelectableValue>(option: SelectItem<Value>) =>
  option.node ?? option.value;

export const Select = <Value extends SelectableValue, CurrentValue extends Value | Value[] = Value>(
  props: SelectProps<Value, CurrentValue>
) => {
  const t = useTranslations('misc.select.any');

  const {
    className,
    variant = 'default',
    value,
    options,
    isMulti: isMultiProp,
    placeholder = t('placeholder'),
    state = 'default',
    disabled = false,
    emptyListNode = t('noOptionsMessage'),
    isSearchable = true,
    menuPlacement = 'auto',
    onChange,
    instanceId: instanceIdProp,
    menuClassName,
    menuPortalTarget,
    fitMenuContent = false,
    closeMenuOnSelect = false,
  } = props;

  const autoInstanceId = useId().replace(/:/g, '');
  const instanceId = instanceIdProp ?? autoInstanceId;

  const [inputValue, setInputValue] = useState('');

  const isMulti = isMultiProp ?? isArray(value);

  const filter = useMemo(() => {
    return createFilter({
      stringify: (option: FilterOptionOption<SelectItem<Value>>) => getNodeText(getOptionDisplayEntity(option.data)),
    });
  }, []);

  const valueInfo = useMemo((): {
    hasValue: boolean;
    libValue: SelectItem<Value> | SelectItem<Value>[] | null;
    formattedValue: ReactNode;
  } => {
    if (!isMulti) {
      const singleValue = value as Value | null | undefined;

      if (!isDefined(value)) {
        return {
          hasValue: false,
          libValue: null,
          formattedValue: placeholder,
        };
      }

      const option = options.find((o) => getRawValue(o.value) === getRawValue(singleValue as SelectableValue));

      return {
        hasValue: isDefined(option),
        libValue: option ?? null,
        formattedValue: option ? (option.selectedNode ?? getNodeText(getOptionDisplayEntity(option))) : placeholder,
      };
    }

    const multiValue = (value ?? []) as Value[];

    const selectedOptions = multiValue
      .map((v: Value) => options.find((option: SelectItem<Value>) => getRawValue(option.value) === getRawValue(v)))
      .filter(isDefined);

    return {
      hasValue: selectedOptions.length > 0,
      libValue: selectedOptions,
      formattedValue:
        selectedOptions
          .map((option) => getNodeText(getOptionDisplayEntity(option)))
          .filter(isDefined)
          .join(', ') || placeholder,
    };
  }, [isMulti, options, placeholder, value]);

  const handleChange = (newValue: OnChangeValue<SelectItem<Value>, boolean>) => {
    if (isArray(newValue)) {
      onChange?.(newValue.map((i) => i.value) as CurrentValue);
    } else if (!newValue && isMulti) {
      onChange?.([] as unknown as CurrentValue);
    } else if (isDefined(newValue?.value)) {
      onChange?.(newValue.value as any);
    }
  };

  return (
    <ReactSelect<SelectItem<Value>, boolean>
      instanceId={instanceId}
      className={clsx(styles.root, className)}
      classNames={{
        control: ({ menuIsOpen }) =>
          clsx(styles.control, styles[`variant_${variant}`], styles[`state_${state}`], {
            [styles.disabled]: disabled,
            [styles.isActive]: menuIsOpen,
            [styles.isNoValue]: !valueInfo.hasValue,
          }),
        input: () => styles.input,
        placeholder: () => styles.placeholder,
        menu: () => clsx(styles.menu, menuClassName, fitMenuContent && styles.menuFitContent),
        menuList: () => styles.menuList,
        menuPortal: () => styles.menuPortal,
        noOptionsMessage: () => styles.noOptionsMessage,
      }}
      value={valueInfo.libValue}
      options={options}
      isMulti={isMulti}
      isSearchable={isSearchable}
      inputValue={inputValue}
      placeholder={valueInfo.formattedValue}
      isDisabled={disabled}
      closeMenuOnSelect={closeMenuOnSelect}
      hideSelectedOptions={false}
      isClearable={isMulti}
      blurInputOnSelect={!isMulti}
      controlShouldRenderValue={false}
      filterOption={filter}
      menuPlacement={menuPlacement}
      menuPortalTarget={menuPortalTarget}
      styles={{
        menu: (base) => ({
          ...base,
          ...(fitMenuContent ? { width: 'max-content', minWidth: 'var(--select-menu-min-width, 360px)' } : null),
        }),
        menuPortal: (base) => ({ ...base, zIndex: 1000000000 }),
      }}
      components={{
        IndicatorSeparator: () => null,
        DropdownIndicator: (state) => (
          <ToggleArrow className={styles.toggleArrow} isOpen={state.selectProps.menuIsOpen} />
        ),
        ClearIndicator: (props) => <CrossIcon className={styles.clearValueIcon} onClick={props.clearValue} />,
        Option: (option) => (
          <Flex
            ref={option.innerRef}
            className={clsx(styles.item, { [styles.selected]: option.isSelected, [styles.focused]: option.isFocused })}
            alignItems="center"
            gap={2}
            {...option.innerProps}
          >
            {isMulti && <Checkbox checked={option.isSelected} />}

            <span className={clsx(styles.itemText, fitMenuContent && styles.itemTextFit)}>
              {getOptionDisplayEntity(option.data) as ReactNode}
            </span>
          </Flex>
        ),
      }}
      unstyled
      noOptionsMessage={() => emptyListNode}
      onInputChange={setInputValue}
      onChange={handleChange}
    />
  );
};
